import {
  MODAL_GA4_PROPERTY,
  MODAL_UPGRADE_PLAN,
} from '@components/App/Modal/constants'
import { ProBadge } from '@components/ProBadge'
import { Button } from '@octopus-do/octopus-storybook'
import { AngleDown } from '@octopus-do/octopus-storybook/icons'
import { Ga4Actions, ga4Selectors } from '@store/reducers/ga4'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useAuth, useProjectAccess } from '@/hooks'
import { GA4_PERMISSION } from '@/permissions/project/definitions'
import { GaIcon } from './components'

export const GaConnect: React.FC = () => {
  const dispatch = useDispatch()

  const { authenticated } = useAuth()
  const viewAccess = useProjectAccess(GA4_PERMISSION.VIEW_METRICS)
  const manageAccess = useProjectAccess(GA4_PERMISSION.MANAGE)

  const status = useSelector(ga4Selectors.getStatus)
  const hostname = useSelector(ga4Selectors.getHostname)
  const connecting = useSelector(ga4Selectors.getConnecting)

  if (!authenticated) {
    return null
  }

  const openIntegrationModal = () => {
    // Список нужен окну для смены проперти, а в статусе ok его никто не
    // грузил — без этого окно висит на «Loading properties…».
    dispatch(Ga4Actions.fetchProperties())
    dispatch(openModal(MODAL_GA4_PROPERTY))
  }

  // Free-владелец: кнопка серая и ведёт на апсейл, а не в Google.
  if (viewAccess.hasNotAvailableAccessBadge) {
    return (
      <Button
        priority="secondary"
        size="medium"
        label="Google Analytics"
        left={GaIcon}
        right={ProBadge}
        onClick={() =>
          dispatch(
            openModal({
              modalType: MODAL_UPGRADE_PLAN,
              modalProps: { slideType: 'seo' },
            })
          )
        }
      />
    )
  }

  if (!viewAccess.hasAccess) {
    return null
  }

  // Единственный момент, когда о подключении неизвестно ничего, — первая
  // загрузка отчёта. Дальше статус хранит последний ответ сервера, и
  // обновления (вход в SEO-режим, resync) кнопку не трогают: у подключённого
  // проекта подмена имени сайта лоадером читалась как переподключение, у
  // неподключённого лоадер мигал на каждом переключении режима.
  if (status === 'idle') {
    return (
      <Button size="medium" label="Google Analytics" left={GaIcon} loading />
    )
  }

  if (status === 'not_connected' || status === 'no_property') {
    if (!manageAccess.hasAccess) {
      return null
    }

    // Неподключённому проекту — то же окно в режиме «Connect»: сначала
    // объяснить, что будет, и только по кнопке уводить на консент Google.
    // Список пропертей до консента запрашивать нечем.
    const onClick =
      status === 'not_connected'
        ? () => dispatch(openModal(MODAL_GA4_PROPERTY))
        : openIntegrationModal

    return (
      <Button
        size="medium"
        label="Google Analytics"
        left={GaIcon}
        loading={connecting}
        onClick={onClick}
      />
    )
  }

  if (status === 'broken' || status === 'error') {
    // Сломанный грант чинится повторным консентом, упавший отчёт — повтором
    // запроса; кнопка для юзера одна и та же.
    const onClick =
      status === 'broken'
        ? () => dispatch(Ga4Actions.connect())
        : () => dispatch(Ga4Actions.fetchReport())

    if (status === 'broken' && !manageAccess.hasAccess) {
      return null
    }

    return (
      <Button
        tone="danger"
        size="medium"
        label="Reconnect"
        left={GaIcon}
        loading={connecting}
        onClick={onClick}
      />
    )
  }

  // Подключено: вся настройка живёт в окне интеграции, кнопка его открывает.
  return (
    <Button
      size="medium"
      label={hostname === '' ? 'Google Analytics' : hostname}
      left={GaIcon}
      right={AngleDown}
      onClick={openIntegrationModal}
    />
  )
}
